import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTrash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import config from '../../config/config';

export function ViewHoldAnimals() {
  const [heldAnimals, setHeldAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. Column-specific search state updated with 'category'
  const [columnSearch, setColumnSearch] = useState({
    id: '',
    type: '',
    category: '', // Added category
    animal_no: '',
    slaughtering_day: '',
    status: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchHeldAnimals();
  }, []);

  const fetchHeldAnimals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/hold/held-animals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setHeldAnimals(data);
    } catch (error) {
      setError('Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setColumnSearch(prev => ({ ...prev, [name]: value }));
  };

  const handleBooking = (animal) => {
    const sharesLeft = parseInt(animal.booked_shares);
    if (sharesLeft <= 0) {
      alert("This animal is fully booked!");
      return;
    }
    navigate(`/hold/${animal.id}`, {
      state: {
        animal: {
          animal_no: animal.animal_no,
          remaining_shares: sharesLeft,
          category: animal.subcat
        }
      }
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${config.baseUrl}/api/hold/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchHeldAnimals();
    } catch (error) {
      alert('Delete failed');
    }
  };

  // 2. Updated Filter Logic: Separated Type and Category
  const filteredAnimals = heldAnimals.filter(animal => {
    const isFull = parseInt(animal.booked_shares) <= 0;
    const statusText = isFull ? 'full' : 'available';

    return (
      String(animal.id).toLowerCase().includes(columnSearch.id.toLowerCase()) &&
      (animal.type?.toLowerCase().includes(columnSearch.type.toLowerCase())) &&
      (animal.category?.toLowerCase().includes(columnSearch.category.toLowerCase())) &&
      (animal.animal_no?.toString().includes(columnSearch.animal_no)) &&
      (String(animal.slaughtering_day).includes(columnSearch.slaughtering_day)) &&
      (statusText.includes(columnSearch.status.toLowerCase()))
    );
  });

  if (loading) return <div className="p-8 text-center font-bold text-indigo-600 animate-pulse text-xl">Loading Held Animals...</div>;

  return (
    <div className="p-4 bg-indigo-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">View Held Animals</h1>
        <div className="text-sm text-gray-500 font-medium">
          Total Found: <span className="text-indigo-600">{filteredAnimals.length}</span>
        </div>
      </div>

      <div className="overflow-x-auto shadow-2xl rounded-md border border-gray-200 bg-indigo-50">
        <table className="min-w-full table-auto">
          <thead>
            {/* Primary Header */}
            <tr className="bg-indigo-900 text-white text-left text-xs uppercase tracking-widest font-bold">
              <th className="p-4 border-r border-indigo-800">ID</th>
              <th className="p-4 border-r border-indigo-800 text-center">Type</th>
              <th className="p-4 border-r border-indigo-800 text-center">Category</th>
              <th className="p-4 border-r border-indigo-800 text-center">Animal No #</th>
              <th className="p-4 border-r border-indigo-800 text-center">Slaughtering Day</th>
              <th className="p-4 border-r border-indigo-800 text-center">Availability</th>
              <th className="p-4 text-center">Actions</th>
            </tr>
            {/* Search Filters Header Row */}
            <tr className="bg-indigo-50 border-b-2 border-indigo-100">
              <td className="p-2 border-r">
                <input name="id" value={columnSearch.id} onChange={handleSearchChange}
                  placeholder="ID" className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400" />
              </td>
              <td className="p-2 border-r">
                <input name="type" value={columnSearch.type} onChange={handleSearchChange}
                  placeholder="Type..." className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400" />
              </td>
              <td className="p-2 border-r">
                <input name="category" value={columnSearch.category} onChange={handleSearchChange}
                  placeholder="Category..." className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400" />
              </td>
              <td className="p-2 border-r ">
                <input name="animal_no" value={columnSearch.animal_no} onChange={handleSearchChange}
                  placeholder="Tag#" className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400" />
              </td>
              <td className="p-2 border-r">
                <select name="slaughtering_day" value={columnSearch.slaughtering_day} onChange={handleSearchChange}
                  className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400">
                  <option value="">All Days</option>
                  <option value="1">Day 1</option>
                  <option value="2">Day 2</option>
                  <option value="3">Day 3</option>
                </select>
              </td>
              <td className="p-2 border-r">
                <select name="status" value={columnSearch.status} onChange={handleSearchChange}
                  className="w-full p-1.5 text-[11px] border rounded shadow-inner outline-none focus:ring-2 focus:ring-indigo-400 font-bold">
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="full">Full / No Shares</option>
                </select>
              </td>
              <td className="p-2 bg-indigo-50"></td>
            </tr>
          </thead>

          <tbody className="divide-y divide-indigo-100">
            {filteredAnimals.length > 0 ? (
              filteredAnimals.map((animal) => {
                const isFull = parseInt(animal.booked_shares) <= 0;
                return (
                  <tr key={animal.id} className={`transition-all duration-200 `}>
                    <td className="p-4 text-xs font-mono text-gray-500">#{animal.id}</td>
                    <td className="p-4 text-center">
                      <p className="font-bold text-gray-800 text-sm uppercase">{animal.type}</p>
                    </td>
                    <td className="p-4 text-center">
                      <p className="text-[10px] text-gray-400 uppercase font-bold">{animal.category}</p>
                    </td>
                    <td className="p-4 text-center">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-100 font-mono font-black text-sm">
                        {animal.animal_no}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-sm font-semibold text-gray-600">Day {animal.slaughtering_day}</span>
                    </td>

                    {/* SHARES STATUS */}
                    <td className="p-4">
                      <div className="flex flex-col items-center">
                        <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm border ${isFull ? 'bg-red-100 text-red-700 border-red-200' : 'bg-green-100 text-green-700 border-green-200'
                          }`}>
                          {isFull ? <FaTimesCircle /> : <FaCheckCircle />}
                          {isFull ? 'No Shares Left' : `${animal.booked_shares} Shares Left`}
                        </span>
                        {/* Progress Bar Visual */}
                        <div className="w-24 h-1.5 bg-gray-200 mt-2 rounded-full overflow-hidden border border-gray-100">
                          <div
                            className={`h-full transition-all duration-700 ease-out ${isFull ? 'bg-red-500' : 'bg-indigo-500'}`}
                            style={{ width: `${(animal.booked_shares / 7) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>

                    <td className="p-4">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => handleBooking(animal)}
                          disabled={isFull}
                          className={`px-4 py-1.5 rounded-lg font-black text-[10px] uppercase shadow-md transition-all transform active:scale-95 ${isFull
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-200'
                            }`}
                        >
                          {isFull ? 'Sold Out' : 'Book Share'}
                        </button>

                        <button
                          onClick={() => handleDelete(animal.id)}
                          className="p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Permanently"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="p-12 text-center text-gray-400 font-medium italic">
                  No animals found matching these search filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}